// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container, Row, Col } from 'reactstrap';
import LoaderHOC from 'components/atoms/LoaderHOC';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import Panel from 'components/atoms/Panel';
import VerticalStepProgressBar from 'components/molecules/VerticalStepProgressBar/';
import VehiclePanel from 'components/atoms/VehiclePanel';
import InfoPanel from 'components/atoms/InfoPanel';
import Disclaimer from 'components/atoms/Disclaimer';
import FieldSwitch from 'components/atoms/FieldSwitch';

import StatusTrackerPageLoader from './StatusTrackerPageLoader';
import './StatusTrackerPage.scss';

const ns = 'status-tracker-page';

type PrivacySettings = {
  emailPrivacySetting: string,
  textPrivacySetting: string
};

export type Props = {
  intl: IntlShape,
  statusTrackerData: Object,
  isEmailPrivacySettingOn: boolean,
  isTextPrivacySettingOn: boolean,
  updateNotificationPreference: (
    notificationType: string,
    action: Boolean
  ) => void,
  setReservationStatusPrivacySettings: PrivacySettings => void
};

class StatusTrackerPage extends React.Component<Props> {
  formatStatusData = (status: string = '') => {
    const { intl } = this.props;
    const appointmentStatus = [
      {
        status: intl.formatMessage({
          id: 'c8.status.tracker.step.arrival'
        })
      },
      {
        status: intl.formatMessage({
          id: 'c8.status.tracker.step.inspection'
        })
      },
      {
        status: intl.formatMessage({
          id: 'c8.status.tracker.step.service'
        })
      },
      {
        status: intl.formatMessage({
          id: 'c8.status.tracker.step.final.inspection'
        })
      },
      {
        status: intl.formatMessage({
          id: 'c8.status.tracker.step.completed'
        }),
        hideCompletedLabel: true
      }
    ];

    switch (status.toLowerCase()) {
      case 'arrived':
        this.setStatus(appointmentStatus, 0, false);
        break;
      case 'inspecting':
        this.setStatus(appointmentStatus, 1, true);
        break;
      case 'servicing':
        this.setStatus(appointmentStatus, 2, true);
        break;
      case 'final inspection':
        this.setStatus(appointmentStatus, 3, true);
        break;
      case 'completed':
        this.setStatus(appointmentStatus, 4, false);
        break;
      default:
        break;
    }

    return appointmentStatus;
  };

  setStatus = (
    appointmentStatus: Array<Object> = [],
    actualStatus: number = 0,
    inProgress: boolean = false
  ) => {
    let i = 0;
    for (i; i <= actualStatus; i++) {
      appointmentStatus[i].isCompleted = true;
    }
    if (inProgress) {
      appointmentStatus[actualStatus].isCompleted = false;
      appointmentStatus[actualStatus].inProgress = true;
    }
    appointmentStatus[actualStatus].current = true;
    return appointmentStatus;
  };

  handleEmailToggle = (value: boolean) => {
    const {
      setReservationStatusPrivacySettings,
      isTextPrivacySettingOn
    } = this.props;
    const privacySettings = {
      emailPrivacySetting: `X,X,X,X,X,X,${value ? 1 : 0}`,
      textPrivacySetting: `X,X,X,X,X,X,${isTextPrivacySettingOn ? 1 : 0}`
    };
    setReservationStatusPrivacySettings(privacySettings);
  };

  handleTextToggle = (value: boolean) => {
    const {
      setReservationStatusPrivacySettings,
      isEmailPrivacySettingOn
    } = this.props;
    const privacySettings = {
      emailPrivacySetting: `X,X,X,X,X,X,${isEmailPrivacySettingOn ? 1 : 0}`,
      textPrivacySetting: `X,X,X,X,X,X,${value ? 1 : 0}`
    };
    setReservationStatusPrivacySettings(privacySettings);
  };

  render() {
    const {
      statusTrackerData: data,
      intl,
      updateNotificationPreference,
      isEmailPrivacySettingOn,
      isTextPrivacySettingOn
    } = this.props;
    const {
      vehicleInfo,
      advisorInfo,
      hasEmailNotificationToggle,
      hasSmsNotificationToggle,
      notifyStatusChange
    } = data;
    const disclaimerKeys = {
      disclaimerText: {
        id: 'c8.status.tracker.disclaimer'
      },
      seeMoreButtonText: { id: 'c8.status.tracker.disclaimer.button.see.more' },
      disclaimerTitle: { id: 'c8.status.tracker.disclaimer.title' },
      modalTitle: { id: 'c8.status.tracker.disclaimer.modal.title' }
    };
    const showUpdatesPanel =
      (hasEmailNotificationToggle || hasSmsNotificationToggle) &&
      !!notifyStatusChange;

    return (
      <AppointmentFlowWizard
        className={ns}
        header={() => {}}
        footer={() => {}}
        isStatusTracker={true}
        body={() => (
          <Container className={`${ns} px-lg-0 px-3`}>
            <LoaderHOC
              serviceId={'statusTracker|data'}
              placeHolderType={() => <StatusTrackerPageLoader />}
              component={() => (
                <React.Fragment>
                  <Row>
                    <Col>
                      <h1 className={`${ns}__title mb-2`}>
                        {intl.formatMessage({
                          id: 'c8.status.tracker.service.tracker'
                        })}
                      </h1>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className={`${ns}__label`}>
                        {intl.formatMessage({
                          id: 'c8.status.tracker.tracking.history'
                        })}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <VehiclePanel vehicleInfo={vehicleInfo} />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Panel
                        className={`${ns}__status-bar`}
                        role={'group'}
                        aria-label={intl.formatMessage({
                          id: 'c8.status.tracker.aria.vehicle.status',
                          defaultMessage: 'Vehicle status information'
                        })}
                      >
                        <VerticalStepProgressBar
                          appointmentStatus={this.formatStatusData(
                            data.appointmentStatus
                          )}
                        />
                      </Panel>
                      <h2 className={`${ns}__label`}>
                        {intl.formatMessage({
                          id: 'c8.status.tracker.your.service.advisor'
                        })}
                      </h2>
                      <Panel>
                        <InfoPanel
                          contactInfo={advisorInfo}
                          ariaGroupLabel={{
                            id: 'c8.status.tracker.aria.advisor.info',
                            defaultMessage: 'Advisor information'
                          }}
                        />
                      </Panel>
                      {showUpdatesPanel && (
                        <div>
                          <h2 className={`${ns}__label`}>
                            {intl.formatMessage({
                              id: 'c8.status.tracker.service.status.updates',
                              defaultMessage: 'SERVICE STATUS UPDATES'
                            })}
                          </h2>
                          <Panel className={`${ns}__status-updates`}>
                            {hasSmsNotificationToggle && (
                              <Row>
                                <Col xs={12}>
                                  <FieldSwitch
                                    label={intl.formatMessage({
                                      id:
                                        'c8.status.tracker.receive.text.message.updates',
                                      defaultMessage:
                                        'Receive text message updates'
                                    })}
                                    name={'text'}
                                    onChange={ev => {
                                      const success = updateNotificationPreference(
                                        'text',
                                        ev.target.checked
                                      );
                                      if (success) {
                                        this.handleTextToggle(
                                          ev.target.checked
                                        );
                                      }
                                    }}
                                    className={'text'}
                                    checked={isTextPrivacySettingOn}
                                  />
                                </Col>
                              </Row>
                            )}
                            {hasEmailNotificationToggle && (
                              <Row>
                                <Col xs={12}>
                                  <FieldSwitch
                                    label={intl.formatMessage({
                                      id:
                                        'c8.status.tracker.receive.email.updates',
                                      defaultMessage: 'Receive email updates'
                                    })}
                                    name={'email'}
                                    onChange={ev => {
                                      const success = updateNotificationPreference(
                                        'email',
                                        ev.target.checked
                                      );
                                      if (success) {
                                        this.handleEmailToggle(
                                          ev.target.checked
                                        );
                                      }
                                    }}
                                    className={'email'}
                                    checked={isEmailPrivacySettingOn}
                                  />
                                </Col>
                              </Row>
                            )}
                          </Panel>
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Disclaimer disclaimerInfo={disclaimerKeys} />
                    </Col>
                  </Row>
                </React.Fragment>
              )}
            />
          </Container>
        )}
      />
    );
  }
}

export default StatusTrackerPage;



// WEBPACK FOOTER //
// ./src/components/pages/StatusTrackerPage/StatusTrackerPage.js